"use client"

import { useRef, useState, Dispatch, SetStateAction } from "react"
import { Message, MessageSegment, ToolCall } from "@/components/chat/types"
import { AgentCoreClient } from "@/lib/agentcore-client"

export interface UseAgentChatArgs {
  /** Configured runtime client, or null while configuration is still loading. */
  client: AgentCoreClient | null
  /** Conversation identifier sent as the runtime session id. */
  sessionId: string
  /**
   * Access-token accessor, read at send time rather than captured at mount so
   * a token renewed mid-conversation is picked up on the next send.
   */
  getAccessToken: () => string | undefined
  /** Loading-state setter. ChatInterface passes GlobalContext's, PanelChat its own. */
  setIsLoading: (loading: boolean) => void
}

export interface UseAgentChatResult {
  messages: Message[]
  setMessages: Dispatch<SetStateAction<Message[]>>
  error: string | null
  setError: (error: string | null) => void
  /**
   * Send one user turn and stream the assistant's reply into `messages`.
   *
   * @param displayMessage The text rendered in the transcript as the user's
   *   message.
   * @param wireMessage Optional override for the prompt actually sent to the
   *   runtime. The embed panel uses this to prepend host-supplied ticket
   *   context without showing it in the transcript; when omitted the display
   *   text is sent verbatim, which is the standalone app's behaviour.
   * @returns True when the turn completed; false when it errored (the error
   *   is already surfaced in `error` and the transcript). The panel uses this
   *   to know whether a context preamble actually reached the runtime.
   */
  sendMessage: (displayMessage: string, wireMessage?: string) => Promise<boolean>
}

/**
 * The streaming chat loop shared by the standalone ChatInterface and the
 * embeddable PanelChat. Owns the transcript and error state, translates the
 * agentcore-client's stream events into message segments, and never forks per
 * consumer: both UIs run exactly this code against exactly the same backend.
 *
 * @param args See {@link UseAgentChatArgs}.
 * @returns Transcript state and the send function. See {@link UseAgentChatResult}.
 */
export function useAgentChat({
  client,
  sessionId,
  getAccessToken,
  setIsLoading,
}: UseAgentChatArgs): UseAgentChatResult {
  const [messages, setMessages] = useState<Message[]>([])
  const [error, setError] = useState<string | null>(null)

  // The session the transcript on screen belongs to, updated every render.
  // A stream in flight captured the sessionId it was started under; if the
  // user switches conversations mid-stream, the two diverge and the stream
  // must stop writing: its updates target "the last message on screen",
  // which now belongs to a different conversation. The turn still completes
  // server-side and lands in its own session's history for replay.
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId

  const sendMessage = async (displayMessage: string, wireMessage?: string): Promise<boolean> => {
    if (!displayMessage.trim() || !client) return false

    const streamSessionId = sessionId
    const isCurrent = () => sessionIdRef.current === streamSessionId

    // Clear any previous errors
    setError(null)

    // Add user message to chat
    const newUserMessage: Message = {
      role: "user",
      content: displayMessage,
      timestamp: new Date().toISOString(),
    }

    setMessages(prev => [...prev, newUserMessage])
    setIsLoading(true)

    // Create placeholder for assistant response
    const assistantResponse: Message = {
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    }

    setMessages(prev => [...prev, assistantResponse])

    try {
      // Get auth token from react-oidc-context
      const accessToken = getAccessToken()

      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      const segments: MessageSegment[] = []
      const toolCallMap = new Map<string, ToolCall>()

      const updateMessage = () => {
        if (!isCurrent()) return
        // Build content from text segments for backward compat
        const content = segments
          .filter((s): s is Extract<MessageSegment, { type: "text" }> => s.type === "text")
          .map(s => s.content)
          .join("")

        setMessages(prev => {
          const updated = [...prev]
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content,
            segments: [...segments],
          }
          return updated
        })
      }

      // User identity is extracted server-side from the validated JWT token,
      // not passed as a parameter: prevents impersonation via prompt injection.
      await client.invoke(wireMessage ?? displayMessage, sessionId, accessToken, event => {
        switch (event.type) {
          case "text": {
            // If text arrives after a tool segment, mark all pending tools as complete
            const prev = segments[segments.length - 1]
            if (prev && prev.type === "tool") {
              for (const tc of toolCallMap.values()) {
                if (tc.status === "streaming" || tc.status === "executing") {
                  tc.status = "complete"
                }
              }
            }
            // Append to last text segment, or create new one
            const last = segments[segments.length - 1]
            if (last && last.type === "text") {
              last.content += event.content
            } else {
              segments.push({ type: "text", content: event.content })
            }
            updateMessage()
            break
          }
          case "tool_use_start": {
            const tc: ToolCall = {
              toolUseId: event.toolUseId,
              name: event.name,
              input: "",
              status: "streaming",
            }
            toolCallMap.set(event.toolUseId, tc)
            segments.push({ type: "tool", toolCall: tc })
            updateMessage()
            break
          }
          case "tool_use_delta": {
            const tc = toolCallMap.get(event.toolUseId)
            if (tc) {
              tc.input += event.input
            }
            updateMessage()
            break
          }
          case "tool_result": {
            const tc = toolCallMap.get(event.toolUseId)
            if (tc) {
              tc.result = event.result
              tc.status = "complete"
            }
            updateMessage()
            break
          }
          case "message": {
            if (event.role === "assistant") {
              for (const tc of toolCallMap.values()) {
                if (tc.status === "streaming") tc.status = "executing"
              }
              updateMessage()
            }
            break
          }
          case "error": {
            // The agent refused or failed before producing output. The server
            // writes this message for the user (access refusals explain
            // themselves), so it goes into the transcript verbatim rather than
            // behind a generic apology. Any pending tool renders as complete
            // so nothing is left spinning.
            for (const tc of toolCallMap.values()) {
              if (tc.status === "streaming" || tc.status === "executing") {
                tc.status = "complete"
              }
            }
            segments.push({ type: "text", content: event.message })
            updateMessage()
            if (isCurrent()) setError(event.message)
            break
          }
        }
      })
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      console.error("Error invoking AgentCore:", err)

      // Update the assistant message with error, unless the user has moved
      // to another conversation: its transcript must not absorb this one's
      // failure.
      if (isCurrent()) {
        setError(`Failed to get response: ${errorMessage}`)
        setMessages(prev => {
          const updated = [...prev]
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content:
              "I apologize, but I encountered an error processing your request. Please try again.",
          }
          return updated
        })
      }
      return false
    } finally {
      // The switch away already reset the loading flag for the new view;
      // clearing it from a background stream would fight the flag's owner.
      if (isCurrent()) setIsLoading(false)
    }
  }

  return { messages, setMessages, error, setError, sendMessage }
}
